import React, { useEffect, useState } from "react";
import { fetchToolboxTalks, toolboxVideoUrl } from "./toolboxApi";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import VideocamOffOutlinedIcon from "@mui/icons-material/VideocamOffOutlined";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { EmptyState, LoadingState } from "../../components/UI/States";

// "2026-08-13T11:01:05.716Z" -> "August 13, 2026"
const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

// Safety toolbox talk videos, newest first. The videos are uploaded from the
// mobile app by the safety team; this page is watch-only.
function ToolboxTalks() {
  const [talks, setTalks] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchToolboxTalks()
      .then((data) => setTalks(Array.isArray(data) ? data : []))
      .catch(() => {
        setError(true);
        setTalks([]);
      });
  }, []);

  return (
    <PageShell size="md">
      <PageHeader
        icon={HealthAndSafetyIcon}
        tone="rose"
        eyebrow="Safety"
        title="Toolbox Talks"
        description="Safety talk videos from the safety team, newest first — the same talks as in the mobile app."
      />

      {talks === null && (
        <div className="app-card rounded-2xl border">
          <LoadingState label="Loading toolbox talks…" />
        </div>
      )}

      {talks !== null && talks.length === 0 && (
        <div className="app-card rounded-2xl border">
          {error ? (
            <EmptyState
              icon={ErrorOutlineIcon}
              title="Couldn't load toolbox talks"
              description="Couldn't load toolbox talks — try again in a minute."
            />
          ) : (
            <EmptyState
              icon={OndemandVideoIcon}
              title="No toolbox talks yet"
              description="No toolbox talks have been posted yet."
            />
          )}
        </div>
      )}

      <div className="space-y-6">
        {(talks || []).map((talk) => (
          <article
            key={talk.id}
            className="app-card overflow-hidden rounded-2xl border"
          >
            {talk.hasVideo ? (
              <video
                className="block aspect-video w-full bg-black"
                controls
                preload="metadata"
                src={toolboxVideoUrl(talk.id)}
              />
            ) : (
              <div className="app-surface flex aspect-video w-full flex-col items-center justify-center gap-2 border-b text-center">
                <VideocamOffOutlinedIcon className="app-muted" />
                <p className="app-muted text-sm">
                  This talk's video isn't available right now.
                </p>
              </div>
            )}
            <div className="p-5">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <h2 className="text-lg font-semibold">{talk.title}</h2>
                {talk.createdAt && (
                  <span className="app-muted text-sm tabular-nums">
                    {formatDate(talk.createdAt)}
                  </span>
                )}
              </div>
              {talk.addedByName && (
                <p className="app-muted mt-0.5 text-sm">
                  Added by {talk.addedByName}
                </p>
              )}
              {talk.description && (
                <p className="mt-3 whitespace-pre-line text-sm leading-relaxed">
                  {talk.description}
                </p>
              )}
            </div>
          </article>
        ))}
      </div>
    </PageShell>
  );
}

export default ToolboxTalks;
